/**
 * Settings → Team — seats, invite, role change, remove.
 * Phone width uses the same set-panels / fld stack as other settings.
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../../context/AuthContext'
import {
  cancelTeamInvite,
  changeTeamRole,
  inviteTeamMember,
  loadTeam,
  removeTeamMember,
  type TeamMember,
} from '../../../api/team'

const INVITE_ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'finance', label: 'Finance' },
] as const

function seatsLabel(rows: TeamMember[], meRole?: string): string {
  const used = rows[0]?.seats_used
  const limit = rows[0]?.seat_limit
  if (used == null || limit == null) return 'Seats'
  return meRole ? `${used} of ${limit} seats · ${meRole}` : `${used} of ${limit} seats used`
}

export default function TeamPanel() {
  const qc = useQueryClient()
  const { user } = useAuth()
  const { data: rows = [], isLoading } = useQuery({ queryKey: ['team'], queryFn: loadTeam })
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('finance')
  const [err, setErr] = useState<string | null>(null)

  const meRole = useMemo(() => {
    const mine = rows.find((r) => r.status === 'active' && r.email && user?.email
      && r.email.toLowerCase() === user.email.toLowerCase())
    return mine?.role
  }, [rows, user?.email])
  const canManage = meRole === 'owner' || meRole === 'admin'
  const atLimit = rows[0] != null && Number(rows[0].seats_used) >= Number(rows[0].seat_limit)

  const inviteMut = useMutation({
    mutationFn: () => inviteTeamMember({ email: email.trim(), role }),
    onSuccess: () => {
      setEmail('')
      setErr(null)
      qc.invalidateQueries({ queryKey: ['team'] })
    },
    onError: (e: Error) => setErr(e.message),
  })

  return (
    <div id="team" className="panel" data-testid="panel-team" style={{ marginTop: 14 }}>
      <div className="panel-h">
        <div className="panel-tt">Team</div>
        <span className="panel-st" data-testid="team-seats">{seatsLabel(rows, meRole)}</span>
      </div>
      <div className="panel-bp">
        {isLoading && <p style={{ fontSize: 13, color: 'var(--ink3)' }}>Loading team…</p>}
        {!isLoading && rows.length === 0 && <p style={{ fontSize: 13, color: 'var(--ink3)' }}>No company membership yet.</p>}
        {rows.map((row) => (
          <TeamRow
            key={`${row.status}-${row.id}`}
            row={row}
            canManage={canManage}
            onChanged={() => qc.invalidateQueries({ queryKey: ['team'] })}
            onError={setErr}
          />
        ))}

        {canManage && (
          <form
            data-testid="team-invite-form"
            onSubmit={(e) => {
              e.preventDefault()
              if (atLimit) {
                setErr(rows[0] ? `${rows[0].seats_used} of ${rows[0].seat_limit} seats used. Ask an admin to add seats.` : 'Seat limit reached.')
                return
              }
              if (!email.trim()) return
              inviteMut.mutate()
            }}
            style={{ marginTop: 16, display: 'grid', gap: 10 }}
          >
            <div className="fld-grid">
              <div className="fld">
                <label className="fld-l" htmlFor="team-invite-email">Invite email</label>
                <input
                  id="team-invite-email"
                  className="fld-i"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@company.com"
                  data-testid="team-invite-email"
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>
              <div className="fld">
                <label className="fld-l" htmlFor="team-invite-role">Role</label>
                <select
                  id="team-invite-role"
                  className="fld-i"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  data-testid="team-invite-role"
                >
                  {INVITE_ROLES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <button className="btn b-dk" type="submit" disabled={inviteMut.isPending || atLimit} data-testid="team-invite-submit">
              {atLimit ? 'Seat limit reached' : inviteMut.isPending ? 'Inviting…' : 'Send invite'}
            </button>
          </form>
        )}
        {err && <p style={{ color: 'var(--red)', fontSize: 12, marginTop: 10 }} data-testid="team-error">{err}</p>}
      </div>
    </div>
  )
}

function TeamRow({
  row, canManage, onChanged, onError,
}: {
  row: TeamMember
  canManage: boolean
  onChanged: () => void
  onError: (msg: string) => void
}) {
  const isOwner = row.role === 'owner'
  return (
    <div
      data-testid={`team-row-${row.email}`}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 8,
        padding: '10px 0',
        borderBottom: '1px solid var(--line)',
      }}
    >
      <div style={{ flex: '1 1 160px', minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{row.name || row.email}</div>
        <div style={{ fontSize: 12, color: 'var(--ink3)' }}>{row.email}</div>
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink2)' }} data-testid={`team-role-${row.email}`}>
        {row.status === 'pending' ? `Pending · ${row.role}` : row.role}
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink4)' }}>
        {row.joined ? new Date(row.joined).toLocaleDateString() : row.status === 'pending' ? '—' : ''}
      </div>
      {canManage && !isOwner && row.status === 'active' && row.user_id != null && (
        <select
          className="fld-i"
          value={row.role}
          aria-label={`Role for ${row.email}`}
          onChange={async (e) => {
            try {
              await changeTeamRole(row.user_id as number, e.target.value)
              onChanged()
            } catch (ex) {
              onError(ex instanceof Error ? ex.message : 'Role change failed')
            }
          }}
        >
          {INVITE_ROLES.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      )}
      {canManage && !isOwner && (
        <button
          type="button"
          className="btn b-ol"
          title={row.status === 'pending' ? 'Cancel invite' : 'Remove member'}
          aria-label={row.status === 'pending' ? 'Cancel invite' : 'Remove member'}
          onClick={async () => {
            try {
              if (row.status === 'pending') await cancelTeamInvite(row.id)
              else if (row.user_id != null) await removeTeamMember(row.user_id)
              onChanged()
            } catch (ex) {
              onError(ex instanceof Error ? ex.message : 'Remove failed')
            }
          }}
        >
          ✕
        </button>
      )}
    </div>
  )
}
